import { useCallback, useState, useEffect } from 'react'
import { Box } from '@mui/material'
import { Typography, Alert } from '@carto/meridian-ds/components'
import { Connection, ConnectionPrivacy, ConnectionConfigType, ProviderType } from 'features/connections'
import { FormattedMessage } from 'react-intl'
import ConnectionShareDialogUi, { ConnectionSharingOptions } from '../../ConnectionShareDialog/ConnectionShareDialogUi'
import { SimplifiedForm } from 'features/common'
import ResourceSharingSection from 'features/common/ui/sharing/SharingSection/ResourceSharingSection'
import FormHeaderSection from '../FormHeaderSection'
import { useGovernanceConnections } from 'features/connections/hooks/useGovernanceConnections'

interface PrivacyFieldFormProps {
  providerId: ProviderType
  connection?: Pick<Connection, 'config' | 'privacy' | 'id' | 'groups' | 'viewerCredentials' | 'sharingScope'>
  form: SimplifiedForm
  connectionConfigType: ConnectionConfigType
}

function PrivacyFieldForm({ form, providerId, connection, connectionConfigType }: PrivacyFieldFormProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const { register, watch, setValue } = form
  const privacy = watch('privacy') || connection?.privacy || ConnectionPrivacy.Private
  const groups = watch('groups') || connection?.groups
  const sharingScope = watch('sharingScope') || connection?.sharingScope
  const viewerCredentials = watch('viewerCredentials') || false
  const { allowSharing } = useGovernanceConnections()

  useEffect(() => {
    register('privacy')
    register('groups')
    register('sharingScope')
    register('viewerCredentials')
  }, [register])

  useEffect(() => {
    if (connection?.privacy) {
      setValue('privacy', connection?.privacy)
    }
  }, [connection?.privacy, setValue])

  useEffect(() => {
    if (connection?.groups) {
      setValue('groups', connection?.groups)
    }
  }, [connection?.groups, setValue])

  useEffect(() => {
    if (connection?.viewerCredentials !== undefined) {
      setValue('viewerCredentials', connection?.viewerCredentials)
    }
  }, [connection?.viewerCredentials, setValue])

  const handleClose = useCallback(() => {
    setDialogOpen(false)
  }, [])

  const handleClickChange = useCallback(() => {
    setDialogOpen(true)
  }, [])

  const handleChangeSharingOptions = useCallback(
    (update: Partial<ConnectionSharingOptions>) => {
      if (update.privacy !== undefined) {
        setValue('privacy', update.privacy, { shouldDirty: true })
      }
      if (update.groups !== undefined) {
        setValue('groups', update.groups)
      }

      if (update.viewerCredentials !== undefined) {
        setValue('viewerCredentials', update.viewerCredentials)
      }

      if (update.sharingScope !== undefined) {
        setValue('sharingScope', update.sharingScope)
      }
    },
    [setValue]
  )

  // ConnectionShareDialog expects up-to-date `connection.privacy`, while ConnectionForm only
  // supplies initial connection object, if we're modyfying existing connection. Let's create
  // an artificial one.
  const editedConnection = {
    id: connection?.id || '',
    privacy,
    groups,
    viewerCredentials,
    provider_id: providerId,
    sharingScope,
    config: {
      ...(connection?.config || { type: connectionConfigType })
    }
  } as Connection

  return (
    <Box mt={4} role="privacy-form-fragment">
      <FormHeaderSection>
        <Typography variant="subtitle2">
          <FormattedMessage id="connections.create.common.permissionsAndSharing" />
        </Typography>
      </FormHeaderSection>
      <Box mt={1.25}>
        <ResourceSharingSection privacy={privacy} groups={groups} onManage={handleClickChange} />
      </Box>
      {viewerCredentials && (
        <Box mt={2}>
          <Alert severity="info">
            <FormattedMessage id="connections.shareDialog.requiresViewerCredential.alertInfo" />
          </Alert>
        </Box>
      )}
      {dialogOpen && (
        <ConnectionShareDialogUi
          connection={editedConnection}
          open={dialogOpen}
          onClose={handleClose}
          onChange={handleChangeSharingOptions}
          allowSharing={allowSharing}
        />
      )}
    </Box>
  )
}

export default PrivacyFieldForm
